import { z } from 'zod';
import { AssessmentStateSchema, DomainSchema, ScoringMethodSchema } from './enums';
import {
  AssessmentIdSchema,
  PackBindingSchema,
  ProjectIdSchema,
  TenantIdSchema,
  UserIdSchema,
} from './ids';
import { NonEmptyStringSchema, TimestampSchema } from './primitives';

/**
 * Reproducibility is a product property (CLAUDE.md principle 6), so temperature
 * is capped by the schema rather than left to per-call discipline. Raising this
 * constant is a conscious, reviewable act; drifting past it by accident is not
 * possible.
 */
export const MAX_REPRODUCIBLE_TEMPERATURE = 0.2;

/**
 * Pinned at run time and copied onto the evidence pack (DESIGN_SPEC §12.2).
 *
 * **`temperature` is nullable because a model may not take one.** It is
 * *removed* rather than deprecated on every model the strong tier routes to
 * (ZD-050 §12), so a non-nullable field would force this pin to name a value
 * that is never sent — the ZD-019 shape exactly: *what does a worker write here
 * when there is nothing to write?* `null` is the pin saying there is no sampling
 * control to pin, and it is distinguishable from `0`, which claims one was asked
 * for. The cap still binds whenever there is a number to cap.
 *
 * **This is a pin, and `llm_calls.temperature` is a record of a call; they are
 * two axes, not one** (ZD-050 §15). One says what the assessment asked for, the
 * other what the request carried. They agree on all of today's data, which is
 * exactly the condition under which merging them would pass every test.
 */
export const LlmConfigSnapshotSchema = z.object({
  provider: NonEmptyStringSchema,
  model: NonEmptyStringSchema,
  temperature: z.number().min(0).max(MAX_REPRODUCIBLE_TEMPERATURE).nullable(),
});
export type LlmConfigSnapshot = z.infer<typeof LlmConfigSnapshotSchema>;

export const ProjectSchema = z.object({
  project_id: ProjectIdSchema,
  tenant_id: TenantIdSchema,
  name: NonEmptyStringSchema,
  created_at: TimestampSchema,
});
export type Project = z.infer<typeof ProjectSchema>;

export const AssessmentSchema = z.object({
  assessment_id: AssessmentIdSchema,
  tenant_id: TenantIdSchema,
  project_id: ProjectIdSchema,
  title: NonEmptyStringSchema,
  state: AssessmentStateSchema,
  scoring_method: ScoringMethodSchema,
  selected_domains: z.array(DomainSchema),

  /* Reproducibility anchors (DESIGN_SPEC §12.2). */
  pinned_model_version: NonEmptyStringSchema,
  pinned_pack_versions: z.array(PackBindingSchema),
  llm_config_snapshot: LlmConfigSnapshotSchema,

  created_by: UserIdSchema,
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type Assessment = z.infer<typeof AssessmentSchema>;

/**
 * One row of `GET /v1/assessments` (docs/DECISIONS.md ZD-120): what a person
 * needs in order to find an assessment and tell it from the others, before they
 * hold its id. A pick of `AssessmentSchema` rather than a second definition,
 * so the list cannot describe an assessment differently from the record.
 *
 * `state` here is the D1 read model's — written by the orchestrator on every
 * successful transition and by nothing else (ZD-017) — and a list is the first
 * thing that has ever read it. It is what a person *navigates* by; anything
 * that *acts* on state asks the Durable Object through `GET /v1/assessments/:id`,
 * which stays authoritative. Staleness in a list is a wrong sentence on a
 * page; staleness in a guard is a wrong transition, which is why the two reads
 * are kept apart rather than one being derived from the other.
 *
 * What is **not** picked, and why: the reproducibility anchors and the
 * scoring method are the assessment's and not the index's, and
 * `llm_config_snapshot` is unproducible from the row as written at creation
 * (ZD-120 §6) — a summary that included it would refuse every assessment ever
 * created.
 */
export const AssessmentSummarySchema = AssessmentSchema.pick({
  assessment_id: true,
  tenant_id: true,
  project_id: true,
  title: true,
  state: true,
  created_by: true,
  created_at: true,
  updated_at: true,
});
export type AssessmentSummary = z.infer<typeof AssessmentSummarySchema>;
